import { AppSdkError } from './sdk-error.mjs';
import { sdkErrorCode, sdkErrorMessage } from './sdk-error-contract.mjs';

/** A client-only presentation on an already-returned failure. Canonical JSON,
 * enumerable fields, status, receipt and refusal/UNKNOWN classification stay
 * unchanged. Strict C8 validation still runs on that same original shape. */
function readableError(error, message) {
  const readable = new AppSdkError(error.code, {}, message).userMessage;
  Object.defineProperties(error, {
    message: { value: readable, configurable: true },
    userMessage: { value: readable, configurable: true },
    toString: { value: () => readable, configurable: true },
  });
  return error;
}

export function sdkFailureMessage(value) {
  const field = value?.error && Object.getOwnPropertyDescriptor(value.error, 'message');
  return field && !field.enumerable ? sdkErrorMessage(field.value) : null;
}

export function presentSdkFailure(value, message = sdkFailureMessage(value)) {
  if (value?.ok !== false || !value.error || typeof value.error !== 'object') return value;
  // Invalid owner codes remain invalid for the canonical result parser; do
  // not convert them into a known pre-dispatch refusal or settled write.
  if (sdkErrorCode(value.error.code, null) === null) return value;
  // Never strip an unknown enumerable field from a canonical failure. Its
  // existing strict parser must still reject it, including an upstream copy.
  if (Object.getOwnPropertyDescriptor(value.error, 'message')?.enumerable) return value;
  const error = { ...value.error };
  return { ...value, error: readableError(error, message) };
}

/** The authenticated host emits this closed error reply. Only its explicit
 * userMessage is copy; a raw Error.message is never reflected into an App. */
export function sdkErrorReply(error, fallback = 'operation_failed') {
  const code = sdkErrorCode(error?.code, fallback);
  const message = new AppSdkError(code, {}, sdkErrorMessage(error?.userMessage)).userMessage;
  // Only the HTTP connection-read adapter sets this private public-detail
  // facet. Generic exception details may contain owner scopes or secrets.
  const detail = error?.readDetail;
  return {
    error: code,
    message,
    ...(detail && typeof detail === 'object' && !Array.isArray(detail) ? { detail } : {}),
  };
}

export function readSdkErrorReply(reply) {
  return new AppSdkError(reply.error, reply.detail ?? {}, reply.message);
}
